import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useTable from '@/hooks/useTable';
import { ErrorBlock, LoadingBlock } from '@/components/feature/SectionState';
import type { GsEvent } from '@/lib/types';

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={`px-3.5 py-1.5 rounded-full text-sm font-label font-semibold uppercase tracking-wide whitespace-nowrap cursor-pointer border transition-colors ${active ? 'bg-foreground-950 text-background-50 border-foreground-950' : 'bg-background-50 text-foreground-800 border-background-300 hover:border-foreground-700'}`}>{label}</button>
  );
}

export default function EventsCalendar() {
  const { data: events, loading, error, reload } = useTable<GsEvent>('gs_events');
  const [city, setCity] = useState('All');
  const [type, setType] = useState('All');

  const visible = useMemo(() => events.filter((e) => e.show_in_calendar !== false), [events]);
  const cities = useMemo(() => Array.from(new Set(visible.map((e) => e.city))), [visible]);
  const types = useMemo(() => Array.from(new Set(visible.map((e) => e.event_type))), [visible]);
  const list = useMemo(() => visible.filter((e) => (city === 'All' || e.city === city) && (type === 'All' || e.event_type === type)), [visible, city, type]);

  return (
    <section id="calendar" className="bg-background-50 px-4 md:px-8 py-16 md:py-24">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-8">
        <div>
          <p className="font-label uppercase tracking-[0.28em] text-xs text-primary-600 font-semibold">Calendar</p>
          <h2 className="font-heading font-bold text-4xl md:text-6xl text-foreground-950 leading-[1.02] mt-3">Upcoming Events</h2>
        </div>
        <div className="flex flex-wrap gap-3 self-start md:self-auto">
          <Link to="/calendar" className="inline-flex items-center gap-2 border border-foreground-950 text-foreground-950 hover:bg-foreground-950 hover:text-background-50 font-label font-bold uppercase tracking-wider px-5 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors">
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-calendar-line"></i></span>Full calendar
          </Link>
          <Link to="/calendar#list-event" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider px-5 py-3 rounded-md whitespace-nowrap cursor-pointer">
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-add-line"></i></span>List your event free
          </Link>
        </div>
      </div>

      {loading && <LoadingBlock label="Loading events" />}
      {error && <ErrorBlock message={error} onRetry={reload} />}

      {!loading && !error && (
        <>
          <div className="flex flex-col gap-3 mb-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-label uppercase text-xs tracking-widest text-foreground-600 w-12">City</span>
              {['All', ...cities].map((c) => <Chip key={c} label={c} active={city === c} onClick={() => setCity(c)} />)}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-label uppercase text-xs tracking-widest text-foreground-600 w-12">Type</span>
              {['All', ...types].map((t) => <Chip key={t} label={t} active={type === t} onClick={() => setType(t)} />)}
            </div>
          </div>

          <div className="border-t-2 border-foreground-950">
            {list.length === 0 && <p className="py-10 text-center text-foreground-600">No events match these filters yet.</p>}
            {list.map((e) => (
              <Link to={`/events/${e.slug}`} key={e.id} className="group grid grid-cols-[72px_1fr] md:grid-cols-[110px_1fr_auto] gap-4 md:gap-8 items-center py-5 border-b border-background-200 hover:bg-background-100 transition-colors px-1 md:px-3 cursor-pointer">
                <div className="text-center bg-foreground-950 text-background-50 rounded-md py-2">
                  <p className="font-label uppercase text-[11px] tracking-widest text-accent-400">{e.weekday}</p>
                  <p className="font-heading font-extrabold text-3xl leading-none">{e.day_label}</p>
                  <p className="font-label uppercase text-xs">{e.month_label} {e.year_label}</p>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg md:text-2xl text-foreground-950 leading-tight group-hover:text-primary-500">{e.name}</h3>
                  <p className="text-sm text-foreground-600 mt-1 flex items-center gap-1.5"><span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line"></i></span>{e.course}, {e.city}</p>
                </div>
                <div className="col-span-2 md:col-span-1 flex flex-wrap gap-2">
                  <span className="bg-secondary-100 text-secondary-900 font-label font-semibold uppercase text-xs tracking-wide px-2.5 py-1 rounded-sm">{e.event_type}</span>
                  <span className={`font-label font-semibold uppercase text-xs tracking-wide px-2.5 py-1 rounded-sm ${e.status === 'Confirmed' ? 'bg-primary-500 text-background-50' : 'bg-accent-100 text-accent-900 border border-accent-300'}`}>{e.status}</span>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </section>
  );
}